/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../../api.js";
import _ from "underscore";
import util from "../../util/util.js";

var uploadSupported = !!('File' in window && 'FileReader' in window && 'FileList' in window && 'Blob' in window);

// Params
// ------
//	 media: 		observableArray to put models for each media item
//	 accept: 		array of mime types to acces
//	 multiple: 		multiple upload items allowed
//	 existingMedia: array of uuids of existing media to popuplate this uploader with


function createMediaModel(file, uuid) {
	var mediaModel = {
		status: ko.observable(),
		progress: ko.observable(),
		url: ko.observable(),
		uuid: ko.observable()
	};

	if(file) {
		try {
			mediaModel.localUrl = window.URL.createObjectURL(file);
			mediaModel.thumbUrl = mediaModel.localUrl;
		} catch(e) {
			console.log(e);
		}
	} else if(uuid) {
		mediaModel.status('complete');
		mediaModel.url(birdata.uploadURL(uuid));
		mediaModel.localUrl = birdata.uploadURL(uuid);
		mediaModel.thumbUrl = mediaModel.localUrl + "/thumb";
		mediaModel.uuid(uuid);
	}

	mediaModel.statusText = ko.pureComputed(function() {
		switch(mediaModel.status()) {
			case 'init':
			case 'uploading':
			case 'finishing':
				return 'Uploading';
			case 'complete':
			default:
				return '';
		}
	});

	mediaModel.progressPath = ko.pureComputed(function() {
		return util.piePath(80, 0.45, mediaModel.progress() || 0);
	});

	mediaModel.showMedia = function() {
		if(mediaModel.url()) {
			window.open(mediaModel.url(), '_blank');
		}
	};

	return mediaModel;
}

var MediaUploaderViewModel = function(params, parent) {
	var self = this;

	params = _.defaults(params, {
		media: params.media || ko.observableArray([]),
		accept: ['image/gif', 'image/jpeg', 'image/png'],
		multiple: true,
		existingMedia: [],
		maxCount: params.multiple ? null : 1
	});

	_.each(params.existingMedia, function(m) {
		params.media.push(existingMediaModel(m));
	});

	function selectFiles() {
		// Show upload dialog
		var input = document.createElement('input');
		input.setAttribute('style', 'display: none');
		input.setAttribute('type', 'file');
		input.setAttribute('accept', params.accept.join(", "));
		if(params.multiple) {
			input.setAttribute('multiple', '');
		}
		document.body.appendChild(input);
		input.addEventListener('change', function() {
			uploadFiles(input.files);
			document.body.removeChild(input);
		});
		input.click();
	};

	function existingMediaModel(uuid) {
		return createMediaModel(undefined, uuid);
	}

	function uploadFiles(files) {
		_.chain(files || [])
			.filter(function(file) {
				return _.contains(['image/gif', 'image/jpeg', 'image/png'], file.type);
			})
			.each(uploadFile);
	}

	function uploadFile(file) {
		if(params.maxCount && params.media().length >= params.maxCount) {
			return;
		}

		var mediaModel = createMediaModel(file);

		birdata.remoteUpload(file, mediaModel.status, mediaModel.progress).then(function(url) {
			mediaModel.url(url);
			mediaModel.uuid(url.split("/").slice(-1)[0]); // a bit hacky to get the uuid from the url
		}).catch(function(e) {
			alert('Upload failed');
			params.media.remove(mediaModel);
		});

		params.media.push(mediaModel);
		return mediaModel;
	}

	function deleteMedia(mediaModel) {
		params.media.remove(mediaModel);
		return true;
	};

	function handleDropEvent(evt) {
		if(evt.dataTransfer) {
			uploadFiles(evt.dataTransfer.files);
		}
	}

	function handlePasteEvent(evt) {
		try {
			if(evt.originalEvent instanceof ClipboardEvent) {
				evt = evt.originalEvent;
			}

			if(!_.contains(evt.clipboardData.types, "text/plain")) {
				uploadFiles(_.map(evt.clipboardData.items, function(item) {
					return item.getAsFile();
				}));
				return false;
			}
		} catch(e) {
			// If anything goes wrong, revert to default behaviour
			console.log("Exception in paste handler, reverting to default behaviour");
		}

		return true;
	}

	self.media = params.media;
	self.selectFiles = selectFiles;
	self.canUpload = ko.pureComputed(function() {
		return uploadSupported && (params.maxCount == null || params.media().length < params.maxCount);
	});
	self.uploadSupported = uploadSupported;
	self.deleteMedia = deleteMedia;
	self.handleDropEvent = handleDropEvent;
	self.handlePasteEvent = handlePasteEvent;
}

ko.components.register('media-uploader', {
	viewModel: function(params) {
		if(params.viewModel) {
			return params.viewModel;
		} else {
			return new MediaUploaderViewModel(params);
		}
	},
	template: require('../../../templates/shared/media-uploader.html')
});


MediaUploaderViewModel.createMediaModel = createMediaModel;

export default MediaUploaderViewModel;
